import type { OrderStatus } from "@bt/domain";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarIcon, PlusIcon, ShoppingBagIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CurrentVehicleNotice } from "@/components/app/current-vehicle";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { OrderStatusBadge } from "@/components/app/status";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { OrderSheet } from "@/features/orders/order-sheet";
import { QuickPurchaseDialog } from "@/features/orders/quick-purchase-dialog";
import { StillToOrder } from "@/features/orders/still-to-order";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { serverToday, useFormat } from "@/lib/format";
import { searchText } from "@/lib/search";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/orders/")({
  validateSearch: z.object({ new: searchText, purchase: searchText }),
  component: OrdersPage,
});

type Order = RouterOutputs["orders"]["list"][number];
const GROUPS: OrderStatus[] = ["draft", "ordered", "partially_received", "received", "cancelled"];

function OrdersPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { vehicleId } = useCurrentVehicle();
  const { data: orders, isLoading } = useQuery(
    trpc.orders.list.queryOptions({ scope: "all", vehicleId }),
  );

  return (
    <Page>
      <PageHeader
        title={t("orders.title")}
        actions={
          <>
            <Button variant="outline" onClick={() => void navigate({ search: { purchase: "1" } })}>
              <ShoppingBagIcon />
              {t("orders.quickPurchase")}
            </Button>
            <Button onClick={() => void navigate({ search: { new: "1" } })}>
              <PlusIcon />
              {t("orders.new")}
            </Button>
          </>
        }
      />
      <CurrentVehicleNotice />
      <div className="flex flex-col gap-7">
        <StillToOrder />
        {isLoading ? (
          <Skeleton className="h-64 rounded-xl" />
        ) : !orders?.length ? (
          <p className="text-sm text-muted-foreground">{t("orders.emptyBody")}</p>
        ) : (
          GROUPS.map((status) => {
            const items = orders.filter((o) => o.status === status);
            if (!items.length) return null;
            return (
              <section key={status}>
                <SectionTitle>
                  {t(`orderStatusGroup.${status}`)}
                  <span className="ml-2 text-sm font-normal text-muted-foreground tabular">
                    {items.length}
                  </span>
                </SectionTitle>
                <Card className="divide-y divide-border">
                  {items.map((order) => (
                    <OrderRow key={order.id} order={order} />
                  ))}
                </Card>
              </section>
            );
          })
        )}
      </div>
      <OrderSheet
        open={search.new === "1"}
        onOpenChange={(open) => void navigate({ search: open ? { new: "1" } : {} })}
        onSaved={(id) =>
          void navigate({ to: "/orders/$orderId", params: { orderId: id }, search: {} })
        }
      />
      <QuickPurchaseDialog
        open={search.purchase === "1"}
        onOpenChange={(open) => void navigate({ search: open ? { purchase: "1" } : {} })}
      />
    </Page>
  );
}

function OrderRow({ order }: { order: Order }) {
  const { t } = useTranslation();
  const format = useFormat();
  const inTransit = order.status === "ordered" || order.status === "partially_received";
  const overdue = inTransit && order.expectedOn !== null && order.expectedOn < serverToday();
  return (
    <Link
      to="/orders/$orderId"
      params={{ orderId: order.id }}
      className="flex flex-col gap-2 px-4 py-3 transition hover:bg-muted/50 sm:flex-row sm:items-center sm:gap-4"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{order.name}</span>
          {order.orderNumber ? (
            <span className="truncate font-mono text-xs text-muted-foreground">
              {order.orderNumber}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
          {order.supplier && order.supplier.name !== order.name ? (
            <span>{order.supplier.name}</span>
          ) : null}
          <span>{t("orders.linesCount", { count: order.lineCount })}</span>
          {order.partNames.length ? (
            <span className="min-w-0 truncate">
              {order.partNames.join(", ")}
              {order.lineCount > order.partNames.length ? " …" : ""}
            </span>
          ) : null}
          {order.status === "draft" ? (
            <span>{t("orders.since", { date: format.date(order.createdAt) })}</span>
          ) : inTransit && order.expectedOn ? (
            <span
              className={cn("flex items-center gap-1", overdue && "font-medium text-missing-fg")}
            >
              <CalendarIcon className="size-3.5" />
              {overdue
                ? t("orders.overdue")
                : t("orders.arrivesIn", { when: format.relativeDate(order.expectedOn) })}
            </span>
          ) : order.receivedOn ? (
            <span>{format.date(order.receivedOn)}</span>
          ) : order.orderedOn ? (
            <span>{format.date(order.orderedOn)}</span>
          ) : null}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5 empty:hidden">
          {order.workOrders.map((wo) => (
            <Badge key={wo.id} tone="outline">
              {wo.title} · {wo.vehicleName}
            </Badge>
          ))}
          {order.forStock ? <Badge tone="outline">{t("orders.forStock")}</Badge> : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {order.totalMinor > 0 ? (
          <span className="text-sm font-medium tabular">
            {format.money(order.totalMinor, order.currency)}
          </span>
        ) : null}
        {order.status === "partially_received" ? (
          <span className="text-xs text-muted-foreground tabular">
            {order.receivedLineCount}/{order.lineCount}
          </span>
        ) : null}
        <OrderStatusBadge status={order.status} />
      </div>
    </Link>
  );
}
